/**
 * SidebarNav — BitDrop app sidebar navigation.
 *
 * BitDrop design spec (sidebar layout CSS):
 *   .sidebar { position:fixed; left:0; top:0; width:85px; height:100vh;
 *              padding:20px 0; display:flex; flex-direction:column;
 *              align-items:center; z-index:11; pointer-events:none;
 *              background:0 0; overflow:hidden; }
 *
 *   .sidebarBackgroundPanel { position:absolute; top:0; left:0;
 *              width:100%; height:100%; background:#fff;
 *              box-shadow:4px 0 16px rgba(0,0,0,.06); }
 *
 *   .sidebarLogo { margin-bottom:20px; }
 *
 *   .navButton { width:60px; height:60px; border-radius:16px;
 *                display:flex; flex-direction:column;
 *                align-items:center; justify-content:center;
 *                cursor:pointer; margin-bottom:4px; }
 *   .navButton--active { background:rgba(64,112,244,0.1); }
 *
 *   .navLabel { font-size:10px; font-weight:600; margin-top:4px; }
 */

import React, { useState, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { Radius, FontWeight, Layout } from '../theme/tokens';
import { Icon } from '../atoms/Icon';
import { PagesPopup } from '../molecules/PagesPopup';
import { SIDEBAR_TABS, SidebarTabId } from '../constants/navigation';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SidebarTab = SidebarTabId;

export interface SidebarNavProps {
  activeTab:      SidebarTab;
  onSelectTab:    (tab: SidebarTab) => void;
  onOpenSettings: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const SidebarNav: React.FC<SidebarNavProps> = ({
  activeTab,
  onSelectTab,
  onOpenSettings,
}) => {
  const { colors, isDark } = useTheme();
  const [showInfo, setShowInfo] = useState(false);
  const [hoveredTab, setHoveredTab] = useState<SidebarTab | null>(null);
  const infoBtnRef = useRef<any>(null);

  return (
    <View style={styles.sidebar}>
      {/* Background panel */}
      <View style={[styles.bgPanel, { backgroundColor: colors.surface, borderRightColor: colors.border }]} />

      {/* Top: logo + nav items */}
      <View style={styles.topItems}>
        <View style={styles.logoWrap}>
          <TouchableOpacity
            style={styles.logoBadge}
            activeOpacity={0.8}
            onPress={() => {
              if (typeof window !== 'undefined') window.location.href = '/';
            }}
          >
            {typeof window !== 'undefined' ? (
              <img
                src="/icons/favicon.svg"
                style={{
                  width: 40,
                  height: 40,
                  filter: isDark ? 'invert(1)' : 'brightness(0)',
                  objectFit: 'contain',
                }}
                alt="BitDrop"
              />
            ) : (
              <Text style={[styles.logoText, { color: colors.text }]}>BD</Text>
            )}
          </TouchableOpacity>
        </View>

        {SIDEBAR_TABS.map(({ tab, icon, label }) => {
          const isActive = activeTab === tab;
          const isHovered = hoveredTab === tab;
          const activeBg = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)';
          const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
          const bg = isActive ? activeBg : isHovered ? hoverBg : 'transparent';
          return (
            <TouchableOpacity
              key={tab}
              style={styles.navBtn}
              onPress={() => {
                onSelectTab(tab);
                setShowInfo(false);
              }}
              activeOpacity={0.75}
              {...{ onMouseEnter: () => setHoveredTab(tab), onMouseLeave: () => setHoveredTab(null) } as any}
            >
              <View style={[styles.iconWrap, { backgroundColor: bg }]}>
                <Icon
                  name={icon}
                  size={24}
                  color={isActive ? colors.brand : colors.subtext}
                />
              </View>
              <Text style={[
                styles.navLabel,
                {
                  color: isActive ? colors.text : colors.subtext,
                  fontWeight: isActive ? FontWeight.bold : FontWeight.medium,
                },
              ]}>
                {label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Bottom: info + settings */}
      <View style={styles.bottomItems}>
        <View ref={infoBtnRef}>
          <TouchableOpacity
            style={styles.bottomNavBtn}
            activeOpacity={0.75}
            onPress={(e) => {
              e.stopPropagation();
              setShowInfo(v => !v);
            }}
          >
            <View style={[
              styles.iconWrap,
              {
                backgroundColor: showInfo
                  ? (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)')
                  : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'),
              },
            ]}>
              <Icon name="info" size={24} color={showInfo ? colors.brand : colors.subtext} />
            </View>
          </TouchableOpacity>
        </View>

        <TouchableOpacity style={styles.bottomNavBtn} onPress={onOpenSettings} activeOpacity={0.75}>
          <View style={[styles.iconWrap, { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }]}>
            <Icon name="settings" size={24} color={colors.subtext} />
          </View>
        </TouchableOpacity>
      </View>

      {/* SSOT Pages Info Popup Molecule */}
      <PagesPopup
        visible={showInfo}
        onClose={() => setShowInfo(false)}
        triggerRef={infoBtnRef}
      />
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  sidebar: {
    position:       'fixed' as any,
    top:            0,
    left:           0,
    width:          Layout.sidebarWidth,   // 85px
    height:         '100vh' as any,
    zIndex:         11,
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'space-between',
    paddingVertical: 20,
    overflow:       'visible' as any,
    pointerEvents:  'none' as any,
  },

  bgPanel: {
    position:       'absolute',
    top:            0,
    left:           0,
    width:          '100%',
    height:         '100%',
    borderRightWidth: 1,
    pointerEvents:  'none' as any,
  },

  topItems: {
    flexDirection:  'column',
    alignItems:     'center',
    width:          '100%',
    pointerEvents:  'auto' as any,
  },

  logoWrap: {
    marginBottom:   20,
    alignItems:     'center',
    justifyContent: 'center',
    pointerEvents:  'auto' as any,
  },

  logoBadge: {
    width:          44,
    height:         44,
    alignItems:     'center',
    justifyContent: 'center',
  },

  logoText: {
    fontSize:   14,
    fontWeight: FontWeight.bold,
  },

  bottomItems: {
    flexDirection:  'column',
    alignItems:     'center',
    gap:            4,
    pointerEvents:  'auto' as any,
  } as any,

  navBtn: {
    width:          60,
    height:         60,
    borderRadius:   Radius.lg,
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    marginBottom:   4,
    pointerEvents:  'auto' as any,
  },

  bottomNavBtn: {
    width:          44,
    height:         44,
    alignItems:     'center',
    justifyContent: 'center',
    pointerEvents:  'auto' as any,
  },

  iconWrap: {
    width:          40,
    height:         40,
    borderRadius:   12,
    alignItems:     'center',
    justifyContent: 'center',
  },

  navLabel: {
    fontSize:   10,
    fontWeight: FontWeight.semibold,
    marginTop:  4,
  },
});
